import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type SyntheticEvent,
} from 'react';
import { View } from 'react-native';

import '@/components/editor/block-editor.web.css';
import './chat.web.css';
import { BlockMentionPicker } from '@/components/chat/BlockMentionPicker.web';
import {
  ChatComposerMentionMirror,
  syncComposerMirrorScroll,
} from '@/components/chat/ChatComposerMentionMirror.web';
import { ChatContextPinChips, primeContextPinTitle } from '@/components/chat/ChatContextPinChips.web';
import { ChatDiscoveryScopeDialog } from '@/components/chat/ChatDiscoveryScopeDialog.web';
import {
  buildConversationLinkTargets,
  collectConversationBlockIds,
} from '@/lib/chat/context-links';
import type { ContextPin, ContextSet, Message } from '@/lib/chat/types';
import { resolveBlockTitles } from '@/lib/databases/relation-titles';
import { applyMentionSelection, findMentionTrigger } from '@/lib/chat/mention-trigger';
import { isDiscoveryEnabled, withDiscoveryEnabled } from '@/lib/chat/scope-tree';
import { useMentionPickerItems } from '@/lib/chat/use-mention-picker-items';
import type { LinkablePage } from '@/lib/blocks/page-link-search';

function FolderIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4.2a1.5 1.5 0 0 1 1.06.44L11.5 7h8A1.5 1.5 0 0 1 21 8.5v9A1.5 1.5 0 0 1 19.5 19h-15A1.5 1.5 0 0 1 3 17.5v-11Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 19V5m0 0-7 7m7-7 7 7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <rect x="6" y="6" width="12" height="12" rx="1.5" />
    </svg>
  );
}

type ChatComposerProps = {
  disabled?: boolean;
  sending?: boolean;
  contextSet: ContextSet;
  messages?: Message[];
  onContextSetChange: (next: ContextSet) => void;
  onSend: (text: string) => void;
  onStop?: () => void;
};

function addPin(contextSet: ContextSet, page: LinkablePage): ContextSet {
  const kind = page.type === 'database' ? 'database' : 'page';
  const title = page.title.trim() || 'Untitled';
  const existing = contextSet.pins.find((pin) => pin.blockId === page.id);
  if (existing) {
    return {
      ...contextSet,
      pins: contextSet.pins.map((pin) =>
        pin.blockId === page.id ? { ...pin, kind, title } : pin,
      ),
    };
  }
  const pin: ContextPin = { kind, blockId: page.id, title };
  return { ...contextSet, pins: [...contextSet.pins, pin] };
}

function readCursor(event: SyntheticEvent<HTMLTextAreaElement>): number {
  return event.currentTarget.selectionStart ?? event.currentTarget.value.length;
}

export function ChatComposer({
  disabled,
  sending,
  contextSet,
  messages = [],
  onContextSetChange,
  onSend,
  onStop,
}: ChatComposerProps) {
  const [text, setText] = useState('');
  const [cursor, setCursor] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [scopeDialogOpen, setScopeDialogOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [resolvedTitles, setResolvedTitles] = useState<Map<string, string>>(new Map());

  const discoveryEnabled = isDiscoveryEnabled(contextSet);
  const scopeCount = contextSet.scopeRoots?.length ?? 0;

  const mentionTrigger = findMentionTrigger(text, cursor);
  const mentionOpen = mentionTrigger != null && !disabled;
  const { items: mentionItems } = useMentionPickerItems(mentionTrigger?.query ?? '');
  const linkTargets = useMemo(
    () => buildConversationLinkTargets(contextSet, messages, resolvedTitles),
    [contextSet, messages, resolvedTitles],
  );

  useEffect(() => {
    const blockIds = collectConversationBlockIds(contextSet, messages);
    if (blockIds.length === 0) {
      setResolvedTitles(new Map());
      return;
    }
    void resolveBlockTitles(blockIds).then((resolved) => {
      const next = new Map<string, string>();
      for (const id of blockIds) {
        const title = resolved.get(id)?.title?.trim();
        next.set(id, title && title.length > 0 ? title : 'Untitled');
      }
      setResolvedTitles(next);
    });
  }, [contextSet, messages]);

  useEffect(() => {
    syncComposerMirrorScroll(textareaRef.current, mirrorRef.current);
  }, [text]);

  useEffect(() => {
    setMentionIndex(0);
  }, [mentionTrigger?.query]);

  useEffect(() => {
    if (mentionItems.length === 0) {
      return;
    }
    setMentionIndex((value) => value % mentionItems.length);
  }, [mentionItems.length]);

  const submit = useCallback(() => {
    const trimmed = text.trim();
    if (!trimmed || disabled || sending) {
      return;
    }
    onSend(trimmed);
    setText('');
    setCursor(0);
  }, [disabled, onSend, sending, text]);

  function syncCursor(event: SyntheticEvent<HTMLTextAreaElement>) {
    setCursor(readCursor(event));
  }

  function onSelectPage(page: LinkablePage) {
    if (!mentionTrigger) {
      return;
    }
    const nextContext = addPin(contextSet, page);
    primeContextPinTitle(page.id, page.title);
    onContextSetChange(nextContext);
    const applied = applyMentionSelection(text, mentionTrigger, cursor, page.title);
    setText(applied.text);
    setCursor(applied.cursor);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(applied.cursor, applied.cursor);
      }
    });
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (mentionOpen && mentionItems.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setMentionIndex((value) => (value + 1) % mentionItems.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setMentionIndex((value) => (value - 1 + mentionItems.length) % mentionItems.length);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        if (mentionTrigger) {
          const nextText = `${text.slice(0, mentionTrigger.start)}${text.slice(cursor)}`;
          setText(nextText);
          setCursor(mentionTrigger.start);
          requestAnimationFrame(() => {
            const el = textareaRef.current;
            if (el) {
              el.focus();
              el.setSelectionRange(mentionTrigger.start, mentionTrigger.start);
            }
          });
        }
        return;
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        const page = mentionItems[mentionIndex];
        if (page) {
          onSelectPage(page);
        }
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        const page = mentionItems[mentionIndex];
        if (page) {
          onSelectPage(page);
        }
        return;
      }
    }

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  }

  return (
    <View className="chat-composer-wrap">
      <ChatContextPinChips contextSet={contextSet} onContextSetChange={onContextSetChange} />
      <div ref={wrapRef} className="chat-composer-input-wrap relative">
        <div className="chat-composer-mirror-host">
          <ChatComposerMentionMirror ref={mirrorRef} text={text} linkTargets={linkTargets} />
        </div>
        <textarea
          ref={textareaRef}
          className="chat-composer-textarea chat-composer-textarea-overlay"
          disabled={disabled || sending}
          placeholder="Message the agent… Type @ to pin a page or database"
          rows={3}
          value={text}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => {
            setText(event.currentTarget.value);
            setCursor(readCursor(event));
            syncComposerMirrorScroll(textareaRef.current, mirrorRef.current);
          }}
          onClick={syncCursor}
          onKeyDown={onKeyDown}
          onKeyUp={syncCursor}
          onSelect={syncCursor}
          onScroll={() => syncComposerMirrorScroll(textareaRef.current, mirrorRef.current)}
        />
        {mentionOpen ? (
          <div className="chat-mention-picker">
            <BlockMentionPicker
              query={mentionTrigger.query}
              selectedIndex={mentionIndex}
              onSelect={onSelectPage}
            />
          </div>
        ) : null}
      </div>
      <View className="mt-2 flex-row items-center justify-between">
        <div className="chat-discovery-controls">
          <button
            type="button"
            role="switch"
            aria-checked={discoveryEnabled}
            className={`chat-discovery-switch${discoveryEnabled ? ' is-on' : ''}`}
            disabled={disabled}
            onClick={() => onContextSetChange(withDiscoveryEnabled(contextSet, !discoveryEnabled))}
          >
            <span className="chat-discovery-switch-track">
              <span className="chat-discovery-switch-thumb" />
            </span>
            <span className="chat-discovery-switch-label">Discover context</span>
          </button>
          {discoveryEnabled ? (
            <button
              type="button"
              className="chat-discovery-scope-btn"
              disabled={disabled}
              title={scopeCount > 0 ? `Discovery scope (${scopeCount})` : 'Discovery scope: whole workspace'}
              aria-label="Edit discovery scope"
              onClick={() => setScopeDialogOpen(true)}
            >
              <FolderIcon />
              {scopeCount > 0 ? (
                <span className="chat-discovery-scope-count">{scopeCount}</span>
              ) : null}
            </button>
          ) : null}
        </div>
        {sending ? (
          <button
            type="button"
            className="chat-composer-action-btn chat-composer-action-btn-stop"
            disabled={disabled}
            aria-label="Stop agent"
            title="Stop agent"
            onClick={() => onStop?.()}
          >
            <StopIcon />
          </button>
        ) : (
          <button
            type="button"
            className="chat-composer-action-btn chat-composer-action-btn-send"
            disabled={disabled || !text.trim()}
            aria-label="Send message"
            title="Send message"
            onClick={submit}
          >
            <SendIcon />
          </button>
        )}
      </View>

      <ChatDiscoveryScopeDialog
        open={scopeDialogOpen}
        contextSet={contextSet}
        onClose={() => setScopeDialogOpen(false)}
        onContextSetChange={onContextSetChange}
      />
    </View>
  );
}
